/**
 * @file Footer.tsx
 * @description Site-wide footer with certification badges, navigation links,
 * contact information, and service links.
 * 
 * Sections:
 * - Certification badges (AMFI, Insurance License)
 * - Brand information
 * - Quick links navigation
 * - Services links
 * - Contact information
 * - Legal links (Privacy Policy, Terms)
 * 
 * @dependencies React Router DOM for Link
 */
import { Link } from 'react-router-dom';

/**
 * Site-wide footer component displaying certifications, contact info,
 * and navigation links.
 */
export default function Footer() {
  return (
    <footer className="bg-gray-900 text-white">
      {/* Certification Badge Section */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 border-b border-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h3 className="text-center text-white font-bold text-lg mb-6">IRDAI-Certified Insurance Advisor</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Health Insurance */}
            <div className="flex items-center justify-center gap-3 bg-emerald-600/90 rounded-lg px-4 py-3 backdrop-blur-sm">
              <div className="bg-white rounded-lg p-2 flex-shrink-0">
                <svg className="w-6 h-6 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="font-bold text-white text-xs">Health Insurance</p>
                <p className="text-emerald-100 text-xs truncate">Care Health: RLH20481559</p>
              </div>
            </div>

            {/* Life Insurance */}
            <div className="flex items-center justify-center gap-3 bg-blue-600/90 rounded-lg px-4 py-3 backdrop-blur-sm">
              <div className="bg-white rounded-lg p-2 flex-shrink-0">
                <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="font-bold text-white text-xs">Life Insurance</p>
                <p className="text-blue-100 text-xs truncate">SBI Life: SLI991485554</p>
              </div>
            </div>

            {/* General Insurance */}
            <div className="flex items-center justify-center gap-3 bg-orange-600/90 rounded-lg px-4 py-3 backdrop-blur-sm">
              <div className="bg-white rounded-lg p-2 flex-shrink-0">
                <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="min-w-0">
                <p className="font-bold text-white text-xs">General Insurance</p>
                <p className="text-orange-100 text-xs truncate">ICICI Lombard: ILG70251</p>
              </div>
            </div>
          </div>

          {/* AMFI Registration */}
          <div className="flex items-center justify-center gap-4 bg-purple-600/90 rounded-lg px-6 py-3 backdrop-blur-sm max-w-2xl mx-auto">
            <div className="bg-white rounded-lg p-2">
              <svg className="w-8 h-8 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="font-bold text-white text-sm">AMFI Registered Mutual Fund Distributor</p>
              <p className="text-purple-100 text-xs">ARN: 349565 | Valid: 30 Dec 2025 - 25 Dec 2028</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1">
            <div className="font-bold text-2xl mb-4">
              <span className="text-red-600">Asset</span> <span className="text-red-600">Wave</span>
            </div>
            <p className="text-gray-400 text-sm">
              Your trusted insurance & financial advisor with 20+ years of experience.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <a href="/#services" className="text-gray-400 hover:text-white transition-colors">
                  Services
                </a>
              </li>
              <li>
                <Link to="/about" className="text-gray-400 hover:text-white transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <a href="/#calculators" className="text-gray-400 hover:text-white transition-colors">
                  Calculators
                </a>
              </li>
              <li>
                <a href="/#testimonials" className="text-gray-400 hover:text-white transition-colors">
                  Testimonials
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Our Services</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/services/health-insurance" className="text-gray-400 hover:text-white transition-colors">
                  Health Insurance
                </Link>
              </li>
              <li>
                <Link to="/services/term-insurance" className="text-gray-400 hover:text-white transition-colors">
                  Term Insurance
                </Link>
              </li>
              <li>
                <Link to="/services/mutual-funds" className="text-gray-400 hover:text-white transition-colors">
                  Mutual Funds
                </Link>
              </li>
              <li>
                <Link to="/services/life-insurance" className="text-gray-400 hover:text-white transition-colors">
                  Life Insurance
                </Link>
              </li>
              <li>
                <Link to="/services/general-insurance" className="text-gray-400 hover:text-white transition-colors">
                  General Insurance
                </Link>
              </li>
              <li>
                <Link to="/services/bonds" className="text-gray-400 hover:text-white transition-colors">
                  Bonds
                </Link>
              </li>
              <li>
                <Link to="/services/mf-loan" className="text-gray-400 hover:text-white transition-colors">
                  Loan
                </Link>
              </li>
              <li>
                <Link to="/services/govt-schemes" className="text-gray-400 hover:text-white transition-colors">
                  Government Schemes
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-center space-x-2 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span>+91 92488 52187</span>
              </li>
              <li className="flex items-center space-x-2 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>contact@assetwaveww.com</span>
              </li>
              <li className="flex items-center space-x-2 text-gray-400">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                <span>WhatsApp: +91 92488 52187</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center text-gray-400 text-sm">
            <p className="mb-4 md:mb-0">&copy; {new Date().getFullYear()} Asset Wave. All rights reserved.</p>
            <div className="flex space-x-6">
              <Link to="/privacy-policy" className="hover:text-white transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms-conditions" className="hover:text-white transition-colors">
                Terms & Conditions
              </Link>
              <a href="/#contact" className="hover:text-white transition-colors">
                Contact
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
